/**
 * Field — label + control + hint/error wrapper for the form primitives.
 *
 * Renders an optional `label` above its children (the control) and an
 * optional `hint` or `error` below it (error takes precedence). Input,
 * Textarea, and Select compose this automatically when given label/hint/error,
 * but it's exported so a page can wrap a custom control in the same layout.
 */
import type { ReactNode, CSSProperties } from 'react';
import { labelStyle, hintStyle, errorTextStyle } from './field-styles';

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** `htmlFor` for the label — pass the control's id so clicking the label focuses it. */
  htmlFor?: string;
  children: ReactNode;
  style?: CSSProperties;
}

export function Field({ label, hint, error, htmlFor, children, style }: FieldProps) {
  return (
    <div style={style}>
      {label != null && (
        <label htmlFor={htmlFor} style={labelStyle}>
          {label}
        </label>
      )}
      {children}
      {error != null ? (
        <div style={errorTextStyle}>{error}</div>
      ) : hint != null ? (
        <div style={hintStyle}>{hint}</div>
      ) : null}
    </div>
  );
}
